import dayjs from 'dayjs'
import { BADGE_MAP, BADGE_TO_NAME_MAP } from '../constant'
import { ParticipantBadge, BadgeName, Participants, Emoji } from 'src/app/types'
import { OnParticipantChangeParticipantType, Participant } from '../types/sdk'
import { toast } from 'react-toastify'

type Toast = {
  kind: 'warning' | 'info' | 'success' | 'error' | 'default'
  message: string
}

export const addToast = ({ kind, message }: Toast) => {
  if (kind === 'default') toast(message)
  else toast[kind](message)
}

export const addErrorSDKToast = (error: object | string) => {
  if (typeof error === 'string') {
    toast['error'](error)
  } else {
    const err = error as Error
    toast['error'](err.message)
  }
}

export const addErrorRTKToast = (error: string) => {
  if (error) {
    toast['error'](error)
  } else {
    toast['error']('Unknown error occurred')
  }
}

export function getBadgeEmojiFromName(badgeName: BadgeName) {
  return BADGE_MAP[badgeName]
}

export function getBadgeNameFromEmoji(emoji: Emoji) {
  return BADGE_TO_NAME_MAP[emoji] as BadgeName
}

export const modifyParticipant = ({
  screenName,
  participantUUID,
}: Participant | OnParticipantChangeParticipantType) => ({
  screenName,
  badges: [],
  participantUUID,
})

export function addBadge({
  currentBadges,
  name,
}: {
  currentBadges: ParticipantBadge[]
  name: BadgeName
}) {
  let badges = currentBadges
  const index = currentBadges.findIndex((badge) => badge.name === name)
  const foundBadge = badges[index]

  if (foundBadge) {
    badges[index].count += 1
  } else {
    // if we've not had this badge before, create it
    badges = [
      ...badges,
      {
        name,
        count: 1,
      },
    ]
  }
  return badges
}

export function setCount({
  currentBadges,
  name,
  count,
}: {
  currentBadges: ParticipantBadge[]
  name: BadgeName
  count: number
}): [ParticipantBadge[], number] {
  let badges = currentBadges
  let countDiff = 0
  const index = currentBadges.findIndex((badge) => badge.name === name)
  const foundBadge = currentBadges[index]

  if (foundBadge) {
    if (count > 0) {
      countDiff = foundBadge.count - count
      foundBadge.count = count
    } else {
      // remove the badge
      badges.splice(index, 1)
      countDiff = foundBadge.count
    }
  }
  return [badges, countDiff]
}

export const extractXmlError = (xml: string) => {
  let defaultError = {
    status: 'CUSTOM_ERROR',
    error: 'unknown',
    data: 'unknown',
  }
  if (typeof xml === 'string') {
    const code = xml.split('<Code>')
    const message = xml.split('<Message>')
    if (code[1]) {
      const codeArr = code[1].split('</Code>')
      defaultError.error = codeArr[0]
    }
    if (message[1]) {
      const messageArr = message[1].split('</Message>')
      defaultError.data = messageArr[0]
    }
  }
  return defaultError
}

// the canvas does not need to be in the react DOM to be usable
// allows usage between useBadgeCanvas and useVirtualBackground, acts as a state container for ImageData
export const canvas = document.createElement('canvas')
export const context = canvas?.getContext('2d', { willReadFrequently: true })!
// obtain the link to the s3 bucket from the session-cookie
// split on `;` as once it is reset from the frontend it will include a `;`
export const getS3Link = () =>
  decodeURIComponent(document.cookie.split('meeting-badges=')[1] || '').split(
    ';'
  )[0]

export const formatDate = (date?: number) => dayjs(date).format('MMM DD, YYYY')

export const formatDateTime = (date?: number) => dayjs(date).format('MMM DD, YYYY hh:mm:ss A')

export const countCharOccurrences = (str: string, char: string) => {
  let count = 0
  for (let i = 0; i < str.length; i++) {
    if (str.charAt(i) === char) {
      count++
    }
  }
  return count
}

// for public assets in the frontend/public dir
export const modifyUrlForProxy = (url: string) => {
  const proxyOrNotUrlString =
    process.env.NODE_ENV === 'development' ? '/api/zoomapp/proxy' : ''

  return proxyOrNotUrlString + url
}

// Merge participants data across all sessions
export const mergeParticipantsData = (
  participants: Participants[]
): Participants => {
  const result: Participants = {}

  for (const participant of participants) {
    for (const screenName in participant) {
      if (!result[screenName]) {
        result[screenName] = participant[screenName]
      } else {
        const badges: ParticipantBadge[] = result[screenName].badges
        for (const badge of participant[screenName].badges) {
          const index = badges.findIndex((b) => b.name === badge.name)
          if (index === -1) {
            badges.push(badge)
          } else {
            badges[index].count += badge.count
          }
        }
      }
    }
  }

  return result
}

export const nameOnObject = (newName: string, object: Record<string, string>) =>
  Object.keys(object).find(
    (name) => name?.toLowerCase() === newName.toLowerCase()
  )
